import { memo, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { PinInput, UnstyledButton } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

import { useAppSelector } from '@xver/hooks/useAppSelector';
import { Button } from '../../../../../components/Atoms';
import { PAGE_TYPE, PageType } from '../../constants';
import { usePhoneNumberVerification } from './hooks/usePhoneNumberVerification';

const formatPhoneNumber = (phoneNumber: string) => {
    const digits = phoneNumber.replace(/\D/g, '').slice(0, 10);

    if (digits.length <= 3) {
        return digits;
    }

    if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const ResendButton = memo<{
    isResendAvailable: boolean;
    resendCountdown: string | null;
    handleResend: () => void;
}>(({ isResendAvailable, resendCountdown, handleResend }) => {
    const { t } = useTranslation('auth');
    return (
        <div className="inline-flex items-center gap-1 mb-2">
            <span className="text-[14px] text-fg/78">{t('phoneVerification.didntReceive')}</span>
            <UnstyledButton onClick={handleResend} disabled={!isResendAvailable}>
                <span
                    className={`text-[14px] ${
                        isResendAvailable
                            ? 'text-blue-400 light:text-blue-700 hover:underline cursor-pointer'
                            : 'cursor-not-allowed text-fg/50'
                    }`}
                >
                    {isResendAvailable
                        ? t('phoneVerification.resend')
                        : t('phoneVerification.resendCountdown', { countdown: resendCountdown })}
                </span>
            </UnstyledButton>
        </div>
    );
});

interface PhoneNumberVerificationFormProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

function PhoneNumberVerificationForm({ setPageType }: PhoneNumberVerificationFormProps) {
    const { t } = useTranslation('auth');
    const errorMessage = useAppSelector((state) => state.error.message);

    const {
        isSubmitting,
        phoneNumber,
        isResendAvailable,
        resendCountdown,
        handleResend,
        setValue,
        watch,
        onSubmit: handleVerifyCode,
    } = usePhoneNumberVerification({
        setPageType,
    });

    const handleCancel = useCallback(() => {
        setPageType(PAGE_TYPE.SIGN_IN);
    }, [setPageType]);

    return (
        <form
            autoComplete="off"
            onSubmit={handleVerifyCode}
            className="w-full flex flex-col justify-center items-center"
        >
            <div className="w-full flex flex-col justify-center items-center">
                <h1 className="text-base font-bold text-fg">{t('phoneVerification.otpTitle')}</h1>
                <span className="text-sm text-fg/78">{t('phoneVerification.otpSubtitle')}</span>
            </div>

            <div className="w-full flex flex-col justify-center items-center gap-6 mt-4">
                <h2 className="text-lg text-fg">
                    {phoneNumber ? formatPhoneNumber(phoneNumber) : t('phoneVerification.notFound')}
                </h2>

                <PinInput
                    size={'sm'}
                    length={6}
                    value={watch('otpCode')}
                    onChange={(value) => setValue('otpCode', value)}
                    styles={{
                        input: {
                            textAlign: 'center',
                        },
                    }}
                />
                <ResendButton
                    isResendAvailable={isResendAvailable}
                    resendCountdown={resendCountdown}
                    handleResend={handleResend}
                />
            </div>

            {errorMessage && (
                <p className="my-2 text-red-400 light:text-red-700 text-sm">{errorMessage}</p>
            )}

            <div className="w-full flex flex-row justify-center items-center gap-2 flex-nowrap">
                <Button
                    type="button"
                    variant={'secondary'}
                    size="sm"
                    className="w-full"
                    onClick={handleCancel}
                >
                    {t('phoneVerification.cancel')}
                </Button>
                <Button
                    type="submit"
                    size="sm"
                    className="w-full"
                    isLoading={isSubmitting}
                    leftIcon={<IconCheck size={16} stroke={1.5} />}
                >
                    {t('phoneVerification.complete')}
                </Button>
            </div>
        </form>
    );
}

export default memo(PhoneNumberVerificationForm);
